'use client';
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { withBlockPageAndRuntime } from '@unifyapps/carbon/no-code/components/BlockRenderer/hocs/withBlockPageAndRuntime';
import { withBlockState } from '@unifyapps/carbon/no-code/components/BlockRenderer/hocs/withBlockState';
import { useBlockRenderBody } from '@unifyapps/carbon/no-code/components/BlockRenderer/useBlockRenderBody';
import { isNative } from '@unifyapps/ui/utils/isNative';
import type React from 'react';
import { memo } from 'react';
import { compose } from '../../../utils/compose';
import type {
  BlockRendererProps,
  BlockRendererWrapperProps,
} from '../../context/RegistryProvider/types';
import { useBlockComputedStateResponder } from './hooks/useBlockComputedStateResponder';
import { withBlockComputations } from './hocs/withBlockComputations';
import { withContext } from './hocs/withContext';
import { withPortalRenderer } from './hocs/withPortalRenderer';
import type { WithBlockComputationsProps } from './types';
import { useBlockRendererCore } from './useBlockRendererCore';
import { maybeWrapInErrorBoundary } from './utils/maybeWrapInErrorBoundary';

const RunnerWithContextPipeline = compose<React.ComponentType<WithBlockComputationsProps>>(
  withContext,
  withBlockComputations,
  isNative() ? undefined : withPortalRenderer,
)(BlockRenderer);

// Typed as `WithBlockComputationsProps` so the JSX call site can mirror the slow
// path (forwarding `slotContextSnapshots`, `blocksRuntimeState`, etc.). The
// underlying `BlockRenderer`/`useBlockRenderBody` only consumes the
// `BlockRendererProps` subset; extras are inert pass-through.
const RunnerWithoutContextPipeline: React.ComponentType<WithBlockComputationsProps> = isNative()
  ? (BlockRenderer as unknown as React.ComponentType<WithBlockComputationsProps>)
  : compose<React.ComponentType<WithBlockComputationsProps>>(
      isNative() ? undefined : withPortalRenderer,
    )(BlockRenderer);

export function BlockRenderer(props: BlockRendererProps) {
  // Mode-agnostic responder for `requestAllBlocksComputedStates`. Same hook
  // also runs from the builder leaf — crucial here because TC inner blocks
  // render in PREVIEW mode (via `useTemplateComponentBlock`'s `innerMode`
  // flip) and wouldn't otherwise have any responder wired up.
  useBlockComputedStateResponder({
    block: props.block,
    computedBlockState: props.computedBlockState,
  });
  return useBlockRenderBody(props);
}

/** IMPORTANT:
 * the order of the runner hocs is important, it helps in preventing unnecessary re-renders
 * at first, we get all the block state, either from the global state store or if the runtime block state is changed, we get it from the runtime block state
 * now, when the block state is changed, we have to re-render, in this situation, withContext, prepares the new context,
 * and BlockRenderer uses it to compute the dynamicPaths of the block
 * In case of notifyChange from other entities, useSyncExternalStore of withContext is used to re-render the block, whenever it triggers a re-render, we know that new context is built.
 * In the end we have all the context and block state ready to compute the visibility and permissions of the block
 */
export const runnerBlockRendererComposer = compose<React.ComponentType<BlockRendererWrapperProps>>(
  memo,
  withBlockPageAndRuntime,
  withBlockState,
  withContext,
  withBlockComputations,
  isNative() ? undefined : withPortalRenderer,
);

export const RunnerBlockRenderer = memo(function RunnerBlockRenderer(
  props: BlockRendererWrapperProps,
) {
  const core = useBlockRendererCore(props, { mode: 'runner' });

  if (core.kind === 'slot') return core.slot;
  if (core.kind === 'null') return null;
  // Runner mode never returns 'placeholder'; defensive narrow.
  if (core.kind === 'placeholder') return null;

  if (core.useWithContextPipeline) {
    return maybeWrapInErrorBoundary(
      <RunnerWithContextPipeline
        {...props}
        block={core.block}
        blockState={core.blockState}
        blockStateId={core.blockStateId}
        blocksRuntimeState={core.blocksRuntimeState}
        context={EMPTY_OBJECT}
        getGlobalStoreContext={EMPTY_FUNCTION}
        slotContextSnapshots={core.slotContextSnapshots}
        updateBlockSessionState={core.updateBlockSessionState}
        updateBlockState={core.updateBlockState}
      />,
      core.shouldSkipErrorBoundary,
    );
  }

  return maybeWrapInErrorBoundary(
    <RunnerWithoutContextPipeline
      {...props}
      block={core.block}
      blockState={core.blockState}
      blockStateId={core.blockStateId}
      // Safe: graph predicate guarantees no dyn/cond paths, so blockState IS the fully-computed state.
      computedBlockState={core.blockState}
      context={EMPTY_OBJECT}
      getGlobalStoreContext={EMPTY_FUNCTION}
      slotContextSnapshots={core.slotContextSnapshots}
      updateBlockSessionState={core.updateBlockSessionState}
      updateBlockState={core.updateBlockState}
    />,
    core.shouldSkipErrorBoundary,
  );
});

export default RunnerBlockRenderer;
